// =============================================================================
// COMPONENTS/CHECKLISTGRUPOS.TSX — config do checklist (grupos → itens) de um estágio
// =============================================================================
// Reutilizável: usado dentro da view de Editar Esteira (por estágio). CRUD de
// grupos e itens (2 níveis, espelho do Ploomes).
// =============================================================================
import { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { pipelinesApi, type ChecklistGrupoParam } from '../services/api';

const ORIGENS: Array<{ v: string; label: string }> = [
  { v: '', label: 'Todas' }, { v: 'QUINTO_ANDAR', label: '5A' }, { v: 'S_QUATRO', label: 'S4' }, { v: 'VAREJO', label: 'VAR' },
];

export default function ChecklistGrupos({ estagioId }: { estagioId: string }) {
  const [grupos, setGrupos] = useState<ChecklistGrupoParam[] | null>(null);
  const [novoGrupo, setNovoGrupo] = useState('');

  const carregar = useCallback(() => {
    setGrupos(null);
    pipelinesApi.listarChecklistsEstagio(estagioId).then(setGrupos).catch(() => setGrupos([]));
  }, [estagioId]);
  useEffect(() => { carregar(); }, [carregar]);

  const patchGrupo = async (id: string, data: Partial<ChecklistGrupoParam>) => {
    setGrupos((gs) => gs?.map((g) => g.id === id ? { ...g, ...data } : g) ?? gs);
    try { await pipelinesApi.atualizarGrupo(id, data); } catch { toast.error('Não foi possível salvar.'); carregar(); }
  };
  const removerGrupo = async (id: string) => {
    if (!confirm('Remover este checklist (grupo) e seus itens?')) return;
    try { await pipelinesApi.excluirGrupo(id); setGrupos((gs) => gs?.filter((g) => g.id !== id) ?? gs); } catch { toast.error('Falha ao remover.'); }
  };
  const addGrupo = async () => {
    if (!novoGrupo.trim()) return;
    try { const g = await pipelinesApi.criarGrupo({ estagioPipelineId: estagioId, nome: novoGrupo.trim(), ordem: (grupos?.length ?? 0) + 1 }); setGrupos((gs) => [...(gs ?? []), { ...g, itens: [] }]); setNovoGrupo(''); }
    catch { toast.error('Falha ao criar checklist.'); }
  };
  const patchItem = async (gid: string, iid: string, data: { nome?: string; obrigatorio?: boolean; ativo?: boolean }) => {
    setGrupos((gs) => gs?.map((g) => g.id === gid ? { ...g, itens: g.itens.map((i) => i.id === iid ? { ...i, ...data } : i) } : g) ?? gs);
    try { await pipelinesApi.atualizarItem(iid, data); } catch { toast.error('Não foi possível salvar o item.'); carregar(); }
  };
  const removerItem = async (gid: string, iid: string) => {
    try { await pipelinesApi.excluirItem(iid); setGrupos((gs) => gs?.map((g) => g.id === gid ? { ...g, itens: g.itens.filter((i) => i.id !== iid) } : g) ?? gs); } catch { toast.error('Falha ao remover item.'); }
  };
  const addItem = async (gid: string, nome: string, ordem: number) => {
    if (!nome.trim()) return;
    try { const it = await pipelinesApi.criarItem({ checklistEtapaId: gid, nome: nome.trim(), ordem: ordem + 1 }); setGrupos((gs) => gs?.map((g) => g.id === gid ? { ...g, itens: [...g.itens, it] } : g) ?? gs); }
    catch { toast.error('Falha ao criar item.'); }
  };

  if (grupos === null) return <div className="flex items-center gap-2 text-otm-txt-3 py-3 text-sm"><Loader2 size={15} className="animate-spin" /> Carregando checklist…</div>;

  return (
    <div className="space-y-2">
      {grupos.length === 0 && <p className="text-[13px] text-otm-txt-3">Nenhum checklist neste estágio.</p>}
      {grupos.map((g) => (
        <GrupoCard key={g.id} grupo={g} onPatch={patchGrupo} onRemove={removerGrupo} onPatchItem={patchItem} onRemoveItem={removerItem} onAddItem={addItem} />
      ))}
      <div className="flex items-center gap-2 pt-1">
        <input value={novoGrupo} onChange={(e) => setNovoGrupo(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addGrupo(); }} placeholder="Novo checklist (grupo)…" className="flex-1 text-[13px] border border-otm-border rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-otm-navy/20" />
        <button onClick={addGrupo} disabled={!novoGrupo.trim()} className="inline-flex items-center gap-1 text-[13px] font-semibold text-white bg-otm-navy px-3 py-1.5 rounded-lg disabled:opacity-40"><Plus size={14} /> Checklist</button>
      </div>
    </div>
  );
}

function GrupoCard({ grupo, onPatch, onRemove, onPatchItem, onRemoveItem, onAddItem }: {
  grupo: ChecklistGrupoParam;
  onPatch: (id: string, d: Partial<ChecklistGrupoParam>) => void;
  onRemove: (id: string) => void;
  onPatchItem: (gid: string, iid: string, d: { nome?: string; obrigatorio?: boolean; ativo?: boolean }) => void;
  onRemoveItem: (gid: string, iid: string) => void;
  onAddItem: (gid: string, nome: string, ordem: number) => void;
}) {
  const [novoItem, setNovoItem] = useState('');
  return (
    <div className={`rounded-lg border p-2.5 ${grupo.ativo ? 'border-otm-border bg-white' : 'border-dashed border-otm-border bg-otm-subtle/40'}`}>
      <div className="flex flex-wrap items-center gap-2">
        <input defaultValue={grupo.nome} onBlur={(e) => { if (e.target.value.trim() && e.target.value !== grupo.nome) onPatch(grupo.id, { nome: e.target.value.trim() }); }} className="flex-1 min-w-[150px] text-[13px] font-semibold text-otm-txt-1 bg-transparent border-b border-transparent focus:border-otm-border focus:outline-none py-0.5" />
        <select value={grupo.origemAplicavel ?? ''} onChange={(e) => onPatch(grupo.id, { origemAplicavel: e.target.value || null })} className="text-[12px] border border-otm-border rounded px-1.5 py-1 bg-white">
          {ORIGENS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
        </select>
        <label className="text-[12px] text-otm-txt-2 inline-flex items-center gap-1"><input type="checkbox" checked={grupo.ativo} onChange={(e) => onPatch(grupo.id, { ativo: e.target.checked })} /> ativo</label>
        <button onClick={() => onRemove(grupo.id)} title="Remover checklist" className="text-otm-txt-3 hover:text-red-600"><Trash2 size={15} /></button>
      </div>
      <div className="mt-2 pl-2 border-l-2 border-otm-subtle space-y-1.5">
        {grupo.itens.length === 0 && <p className="text-[12px] text-otm-txt-3">Sem itens.</p>}
        {grupo.itens.map((it) => (
          <div key={it.id} className="flex items-center gap-2">
            <input defaultValue={it.nome} onBlur={(e) => { if (e.target.value.trim() && e.target.value !== it.nome) onPatchItem(grupo.id, it.id, { nome: e.target.value.trim() }); }} className="flex-1 text-[13px] text-otm-txt-2 bg-transparent border-b border-transparent focus:border-otm-border focus:outline-none py-0.5" />
            <label className="text-[11px] text-otm-txt-3 inline-flex items-center gap-1"><input type="checkbox" checked={it.obrigatorio} onChange={(e) => onPatchItem(grupo.id, it.id, { obrigatorio: e.target.checked })} /> obrigatório</label>
            <button onClick={() => onRemoveItem(grupo.id, it.id)} title="Remover item" className="text-otm-txt-3 hover:text-red-600"><Trash2 size={13} /></button>
          </div>
        ))}
        <div className="flex items-center gap-2 pt-1">
          <input value={novoItem} onChange={(e) => setNovoItem(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { onAddItem(grupo.id, novoItem, grupo.itens.length); setNovoItem(''); } }} placeholder="Novo item…" className="flex-1 text-[12px] border border-otm-border rounded px-2 py-1 focus:outline-none" />
          <button onClick={() => { onAddItem(grupo.id, novoItem, grupo.itens.length); setNovoItem(''); }} disabled={!novoItem.trim()} className="inline-flex items-center gap-1 text-[12px] text-otm-navy font-medium px-2 py-1 rounded hover:bg-otm-navy/5 disabled:opacity-40"><Plus size={13} /> item</button>
        </div>
      </div>
    </div>
  );
}
